import { I18n } from "@assets/strings";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { countries } from "@domain/currentProfile/countryCode";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { Organization } from "@domain/organization/organization";
import { useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { CategoriesView } from "@ui/components/categoriesView";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CountryFlag } from "@ui/components/CountryFlag";
import { CalendarIcon, ChevronDownIcon, ClipIcon, MailIcon, MapIcon, PhoneIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { LinkExternal } from "@ui/components/linkExternal";
import { MenuRow } from "@ui/components/menus/menuRow";
import { CategoriesEditionModal } from "@ui/components/profile/categoriesEditionModal";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, gap, mobile } from "@ui/styles/utils";

interface OrganizationDetailsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onToggle"> {
	organization: Organization;
	onToggle?: (opened: boolean) => void;
}

export const OrganizationDetails: React.FC<OrganizationDetailsProps> = ({ organization, onToggle, ...htmlProps }) => {
	const { address, countryCode, email, phoneNumber, webSite, beginDate, categories } = organization;
	const creationYear = beginDate ? new Date(beginDate).getFullYear() : undefined;
	const organizationAlias = useOrganizationAlias();
	const isMobile = useIsMobile();
	const [opened, setOpened] = useState(!isMobile);
	const { createModal } = useModalTools();
	const { organizationService } = useServices();

	useEffect(() => {
		setOpened(!isMobile);
	}, [isMobile]);

	useEffect(() => {
		onToggle?.(opened);
	}, [opened]);

	const openEditionModal = (): void =>
		createModal((close) => (
			<CategoriesEditionModal
				initialCategories={categories ?? []}
				close={close}
				title={I18n.t(`organization.categories.${organization.type.toLowerCase()}.title`)}
				saveCategories={(categories) =>
					organizationService.save(
						{
							...organization,
							categories,
						},
						organization?.id
					)
				}
			/>
		));

	const fixedWebsite = useMemo(() => {
		if (!webSite || webSite.startsWith("http")) {
			return webSite;
		}
		return `http://${webSite}`;
	}, [webSite]);

	return (
		<Container {...htmlProps}>
			<MenuHeader opened={opened} onClick={() => setOpened(!opened)}>
				<Primary>{I18n.t("organization.details.mobile_title")}</Primary>
				<ChevronDownIcon />
			</MenuHeader>
			{opened ? (
				<>
					{creationYear && (
						<MenuRow>
							<CalendarIcon />
							<OrganizationItem>
								{I18n.t("organization.details.year_of_creation.label", { year: creationYear })}
							</OrganizationItem>
						</MenuRow>
					)}
					<MenuRow>
						<MapIcon />
						<OrganizationItem>{address ?? "-"}</OrganizationItem>
					</MenuRow>
					{countryCode && (
						<MenuRow>
							<CountryFlag countryCode={countryCode} />
							<OrganizationItem>{countries[countryCode]}</OrganizationItem>
						</MenuRow>
					)}
					<MenuRow>
						<MailIcon />
						<OrganizationItem>{email ? <a href={`mailto:${email}`}>{email}</a> : "-"}</OrganizationItem>
					</MenuRow>

					<MenuRow>
						<PhoneIcon />
						<OrganizationItem>{phoneNumber ?? "-"}</OrganizationItem>
					</MenuRow>
					<MenuRow>
						<ClipIcon />
						<OrganizationItem>
							{fixedWebsite ? <LinkExternal to={fixedWebsite}>{webSite}</LinkExternal> : "-"}
						</OrganizationItem>
					</MenuRow>
					<CategoriesContainer>
						{categories && categories.length > 0 && <CategoriesView categories={categories} />}
						{!!organizationAlias && (
							<>
								{categories && categories.length > 0 ? (
									<TertiaryButton onClick={openEditionModal}>
										{I18n.t("organization.details.categories.edit")}
									</TertiaryButton>
								) : (
									<AddButton onClick={openEditionModal}>
										{I18n.t(`organization.details.categories.${organization.type.toLowerCase()}.add`)}
									</AddButton>
								)}
							</>
						)}
					</CategoriesContainer>
				</>
			) : null}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	padding: 45px 34px;
	${gap(40)};
	@media ${mobile} {
		padding: 15px;
		${gap(20)}
	}
`;

const MenuHeader = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "opened",
})<{ opened: boolean }>`
	${row("S", "space-between", "center")};
	svg {
		flex-shrink: 0;
		${({ opened }) => opened && "transform: rotate(180deg)"};
	}
	@media ${desktop} {
		display: none;
	}
`;

const OrganizationItem = styled.div`
	text: ${({ theme }) => theme.textStyles.light};
	word-break: break-word;
`;

const CategoriesContainer = styled.div`
	> button {
		margin: auto;
		margin-top: 20px;
	}
`;

const AddButton = styled(PrimaryButton)`
	padding: 12px;
`;
